import { useCallback, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { STRIPE_PUBLISHABLE_KEY } from "../supabaseClient"
import { useOrganization } from "../OrganizationContext"
import { organizationTheme } from "../utils/organizationProfile"
import {
  getOrganizationBillingSummary,
  getResumePaymentIntent,
  initializeStripeCheckout,
} from "../api/stripeBilling"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui"

const stripePromise = STRIPE_PUBLISHABLE_KEY
  ? loadStripe(STRIPE_PUBLISHABLE_KEY)
  : null

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

function formatCents(value) {
  return currency.format((Number(value) || 0) / 100)
}

function ResumePaymentForm({ organizationId, onComplete }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  const submit = async (event) => {
    event.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError("")
    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/organizations/${organizationId}/plan`,
        },
      })
      if (result.error) {
        setError(result.error.message || "Payment authentication failed.")
        setSubmitting(false)
        return
      }
      await onComplete()
    } catch (confirmationError) {
      setError(
        confirmationError?.message || "Payment authentication failed.",
      )
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      {error && <p className="ui-alert ui-alert--danger">{error}</p>}
      <Button type="submit" disabled={!stripe || submitting}>
        {submitting ? "Confirming payment…" : "Confirm payment"}
      </Button>
    </form>
  )
}

export default function OrganizationCheckout() {
  const { id } = useParams()
  const { organizations } = useOrganization()
  const appName = organizationTheme(
    organizations.find((organization) => String(organization.id) === String(id)),
  ).appName
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const requestedPurpose = searchParams.get("purpose")
  const purpose =
    requestedPurpose === "setup_payment_method"
      ? "setup_payment_method"
      : "subscribe"
  const [summary, setSummary] = useState(null)
  const [status, setStatus] = useState(null)
  const [error, setError] = useState("")
  const [paymentIntentSecret, setPaymentIntentSecret] = useState(null)
  const billableUnit = summary?.organization_type === "standard" ? "account" : "dealership"

  const refreshUntilSettled = useCallback(async () => {
    setStatus("Finalizing your billing setup…")
    try {
      for (let attempt = 0; attempt < 12; attempt += 1) {
        const nextSummary = await getOrganizationBillingSummary(id)
        if (nextSummary) setSummary(nextSummary)
        if (
          ["active", "trialing"].includes(nextSummary?.stripe_status) &&
          nextSummary?.access_state === "active"
        ) {
          setStatus(
            nextSummary.stripe_status === "trialing"
              ? "Your seven-day trial is active."
              : "Your subscription is active.",
          )
          setPaymentIntentSecret(null)
          return
        }
        if (
          purpose === "setup_payment_method" &&
          (["paused", "past_due"].includes(nextSummary?.stripe_status) ||
            nextSummary?.access_state === "suspended")
        ) {
          const paymentAction = await getResumePaymentIntent(id)
          if (
            paymentAction?.status === "action_required" &&
            paymentAction?.client_secret
          ) {
            setPaymentIntentSecret(paymentAction.client_secret)
            setStatus("One more authentication step is required.")
            return
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 1500))
      }
      setStatus(
        "Stripe is still finalizing the subscription. You can safely return to the plan page and refresh.",
      )
    } catch (refreshError) {
      setError(
        refreshError?.message ||
          "We couldn't verify the updated billing state. Please return to the plan page and refresh.",
      )
    }
  }, [id, purpose])

  const fetchClientSecret = useCallback(async () => {
    setError("")
    try {
      const session = await initializeStripeCheckout(id, { purpose })
      setSummary(session.summary || null)
      return session.clientSecret
    } catch (checkoutError) {
      setError(
        checkoutError?.message || "Unable to initialize embedded checkout.",
      )
      throw checkoutError
    }
  }, [id, purpose])

  const checkoutOptions = useMemo(
    () => ({
      fetchClientSecret,
      onComplete: refreshUntilSettled,
    }),
    [fetchClientSecret, refreshUntilSettled],
  )

  if (!stripePromise) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Billing configuration required</CardTitle>
          <CardDescription>
            REACT_APP_STRIPE_PUBLISHABLE_KEY is not configured.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <main className="organization-checkout" style={{ maxWidth: 1040, margin: "0 auto", padding: "2rem 1rem" }}>
      <Button
        type="button"
        variant="secondary"
        onClick={() => navigate(`/organizations/${id}/plan`)}
      >
        Back to plans
      </Button>

      <Card style={{ marginTop: "1rem", marginBottom: "1rem" }}>
        <CardHeader>
          <Badge>
            {purpose === "subscribe" ? "Paid plan" : "Payment method"}
          </Badge>
          <CardTitle>
            {purpose === "subscribe"
              ? `Start billing without leaving ${appName}`
              : "Add a payment method and resume"}
          </CardTitle>
          <CardDescription>
            {purpose === "subscribe"
              ? summary?.trial_eligible
                ? "Your first seven days are free. A card is not required to begin the trial."
                : summary
                  ? "This organization has already used its trial, so billing begins immediately."
                  : "Review and complete your secure billing setup below."
              : "Your card is collected securely by Stripe inside this page."}
          </CardDescription>
        </CardHeader>
        {summary && (
          <CardContent>
            <p>
              <strong>{summary.quantity}</strong>{" "}
              {summary.quantity === 1 ? billableUnit : `${billableUnit}s`} ×{" "}
              <strong>{formatCents(summary.unit_amount_cents)}</strong> per month
            </p>
            <p>
              Estimated monthly total:{" "}
              <strong>{formatCents(summary.monthly_total_cents)}</strong>
            </p>
            {summary.trial_eligible && <p>Due today: <strong>$0</strong></p>}
          </CardContent>
        )}
      </Card>

      {error && <p className="ui-alert ui-alert--danger">{error}</p>}
      {status && (
        <Card style={{ marginBottom: "1rem" }}>
          <CardContent>
            <p>{status}</p>
            {status.includes("active") && (
              <Button onClick={() => navigate(`/organizations/${id}/plan`)}>
                Return to plan
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {paymentIntentSecret ? (
        <Card>
          <CardHeader>
            <CardTitle>Confirm your first payment</CardTitle>
          </CardHeader>
          <CardContent>
            <Elements
              stripe={stripePromise}
              options={{ clientSecret: paymentIntentSecret }}
            >
              <ResumePaymentForm
                organizationId={id}
                onComplete={refreshUntilSettled}
              />
            </Elements>
          </CardContent>
        </Card>
      ) : (
        <EmbeddedCheckoutProvider stripe={stripePromise} options={checkoutOptions}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}
    </main>
  )
}
